import { Component, OnInit, Inject, LOCALE_ID, ViewChild } from '@angular/core';
import { toTitle } from 'src/utils/toTitleCase';
import { PaymentService } from 'src/services/payment/payment.service';
import { KPIService } from 'src/services/kpi/kpi.service';
import { Payment, sortPaymentsByDueDate } from 'src/classes/payment';
import { UserService } from 'src/services/user/user.service';

@Component({
  selector: 'app-user-billing-page',
  templateUrl: './user-billing-page.component.html',
  styleUrls: ['./user-billing-page.component.scss']
})
export class UserBillingPageComponent implements OnInit {

  public error = {
    active: false,
    message: "",
    stack: ""
  }

  public plan: {
    expirationDate: Date,
    name: string,
    deliveriesQuantity: number
  }

  public invoiceList: {
    id: string,
    payment: Payment;
    statusString: string;
    paid: boolean;
    paidString: string,
    billingDateString: string;
    billingValueString: string
    dueDateString: string,
    billingUrl: string,
    description: string,
  }[];

  public billingDetails: {
    company: string;
    paymentMethod: string;
    nextBillingDate: string;
    country: string;
    billingDay?: number;
    editing?: boolean;
  }

  public columns = [
    {
      label: "Id",
      property: "id",
      isLoading: false,
      filter: true,
      width: "10%"
    }, {
      label: "Status",
      property: "statusString",
      isLoading: false,
      filter: true,
      width: "20%"
    }, {
      label: "Billing Date",
      property: "billingDateString",
      isLoading: false,
      filter: true
    }, {
      label: "Due Date",
      property: "dueDateString",
      isLoading: false,
      filter: true
    }, {
      label: "Description",
      hidden: true,
      property: ["payment", "description"],
      isLoading: false,
      filter: true
    }
  ];

  public hasCopied = false;

  @ViewChild('descriptionText', { static: false }) descriptionText;

  constructor(
    private kpiService: KPIService,
    private userService: UserService,
    private paymentService: PaymentService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngOnInit() {
    this.updateInvoiceList().catch(this.applyError.bind(this));
    this.updatePlanDetails().catch(this.applyError.bind(this));
    this.updateBillingDetails().catch(this.applyError.bind(this));
    this.applyLocaleToHeader();
  }

  private applyLocaleToHeader() {
    if (typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt") {
      const portugueseList = ["Id", "Status", "Data de Emissão", "Data de Vencimento", "Descrição" ];

      portugueseList.forEach((portugueseLabel, index) => this.columns[index].label = portugueseLabel);
    }
  }

  onUpdateInvoiceHistoryClick() {
    this.invoiceList = undefined;
    this.updateInvoiceList().catch(this.applyError.bind(this));
  }

  async updatePlanDetails() {
    await new Promise(resolve => setTimeout(resolve, 300));
    this.plan = await this.fetchPlanData();
  }

  async fetchPlanData() {
    const data = await this.kpiService.getKPIs();
    return {
      expirationDate: data.plan.planExpiration,
      name: data.plan.name,
      deliveriesQuantity: data.plan.deliveriesQuantity
    };
  }

  applyError(err) {
    this.error.active = true;
    this.error.message = err.message;
    if (err.stack) {
      this.error.stack = err.stack;
    }
  }

  async updateInvoiceList() {
    this.invoiceList = await this.fetchInvoiceList();
  }

  async updateBillingDetails() {
    this.billingDetails = await this.fetchBillingDetails();
  }

  async fetchBillingDetails(): Promise<any> {
    const user = await this.userService.get();
    return {
      company: user.paramValues.company,
      paymentMethod: "billet",
      nextBillingDate: null,
      country: user.paramValues.country,
      billingDay: 20
    }
  }

  async fetchInvoiceList() {
    const list = await this.paymentService.listCurrentUserPayments();
    const isPortuguese = (typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt");
    return sortPaymentsByDueDate(list, true).map(payment => ({
      id: payment.id,
      payment: payment,
      paid: payment.status !== "O",
      paidString: (payment.status === "P") ? (isPortuguese ? "Sim" : "Yes") : (isPortuguese ? "NÃ£o" : "No"),
      statusString: (payment.status === "B" || payment.status === "O") ? (isPortuguese ? "Pendente" : "Pending") : (isPortuguese ? "Pago" : "Paid"),
      billingValueString: 'R$ ' + payment.amount.toFixed(2),
      billingDateString: payment.billingDate.toLocaleDateString(),
      dueDateString: payment.dueDate.toLocaleDateString(),
      billingUrl: payment.urlBilling,
      description: payment.description
    }))
  }

  onOpenPaymentClick() {

  }

  onCopyDocumentClick() {
    if (!this.descriptionText || !this.descriptionText.nativeElement) {
      console.warn("No description text ref");
      return;
    }
    const elem = this.descriptionText.nativeElement;
    if (!elem) {
      console.warn("No native element on description");
      return;
    }
    elem.parentNode.style.display = "block";
    elem.select();
    elem.setSelectionRange(0, elem.value.length);
    document.execCommand("copy");
    elem.parentNode.style.display = "";
    this.hasCopied = true;
  }

  onEditBillingDetailsClick() {
    this.billingDetails.editing = true;
  }

  onCancelEditBillingDetailsClick() {
    this.billingDetails.editing = false;
  }

  onSaveBillingDetailsClick() {
    if (!this.billingDetails.editing) {
      return;
    }

    this.billingDetails.editing = false;
  }

  getFriendlyPaymentString(payment: string) {
    return toTitle(payment.split('@')[0].replace(/[^a-zA-Z0-9]/g, ' ').trim());
  }

}
